<script setup>
import { computed, h, watch, ref } from 'vue';
import { format, isValid, parse, parseISO } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import FilterSelect from './inputs/FilterSelect.vue';
import MultiSelect from './inputs/MultiSelect.vue';
import SingleSelect from './inputs/SingleSelect.vue';
import DateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';

import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { validateSingleFilter } from 'dashboard/helper/validations.js';

// filterTypes: import('vue').ComputedRef<FilterType[]>
const { filterTypes } = defineProps({
  position: { type: Number, default: 0 },
  showQueryOperator: { type: Boolean, default: false },
  filterTypes: { type: Array, required: true },
});

const emit = defineEmits(['remove']);
const { t } = useI18n();
const showErrors = ref(false);

const attributeKey = defineModel('attributeKey', {
  type: String,
  required: true,
});

const values = defineModel('values', {
  type: [String, Number, Array, Object],
  required: true,
});

const filterOperator = defineModel('filterOperator', {
  type: String,
  required: true,
});

const queryOperator = defineModel('queryOperator', {
  type: String,
  required: false,
  default: undefined,
  validator: value => ['and', 'or'].includes(value),
});

const getFilterFromFilterTypes = key =>
  filterTypes.find(filterObj => filterObj.attributeKey === key);

const currentFilter = computed(() =>
  getFilterFromFilterTypes(attributeKey.value)
);

const getOperator = (filter, selectedOperator) => {
  const operatorFromOptions = filter?.filterOperators?.find(
    operator => operator.value === selectedOperator
  );

  if (!operatorFromOptions) {
    return filter?.filterOperators?.[0];
  }

  return operatorFromOptions;
};

const currentOperator = computed(() =>
  getOperator(currentFilter.value, filterOperator.value)
);

const getInputType = (operator, filter) =>
  operator.inputOverride ?? filter.inputType;

const inputType = computed(() =>
  getInputType(currentOperator.value, currentFilter.value)
);

const queryOperatorOptions = computed(() => {
  return [
    {
      label: t(`FILTER.QUERY_DROPDOWN_LABELS.AND`),
      value: 'and',
      icon: h('span', { class: 'i-lucide-ampersands !text-n-blue-11' }),
    },
    {
      label: t(`FILTER.QUERY_DROPDOWN_LABELS.OR`),
      value: 'or',
      icon: h('span', { class: 'i-woot-logic-or !text-n-blue-11' }),
    },
  ];
});

const booleanOptions = computed(() => [
  { id: true, name: t('FILTER.ATTRIBUTE_LABELS.TRUE') },
  { id: false, name: t('FILTER.ATTRIBUTE_LABELS.FALSE') },
]);

const validationError = computed(() => {
  // TOOD: Migrate validateSingleFilter to use camelcase and then remove useSnakeCase here too
  return validateSingleFilter(
    useSnakeCase({
      attributeKey: attributeKey.value,
      filterOperator: filterOperator.value,
      values: values.value,
    })
  );
});

const inputFieldType = computed(() => {
  if (inputType.value === 'number') return 'number';
  return 'text';
});

const selectedDate = computed(() => {
  if (inputType.value !== 'date' || !values.value) return null;

  const rawValue = String(values.value);
  const parsedDate = rawValue.includes('/')
    ? parse(rawValue, 'dd/MM/yyyy', new Date())
    : parseISO(rawValue);

  return isValid(parsedDate) ? parsedDate : null;
});

const updateDate = date => {
  values.value = date && isValid(date) ? format(date, 'yyyy-MM-dd') : '';
};

const asyncOptions = ref([]);
const isSearching = ref(false);
const lastSearchQuery = ref('');

const performAsyncSearch = async query => {
  let results;
  try {
    results = await currentFilter.value.searchOptions(query);
  } catch {
    results = [];
  }
  // skip stale responses — a newer search in this row owns the UI
  if (query !== lastSearchQuery.value) return;
  // null means another row's search aborted ours, reset instead of staying stuck on the searching state
  if (results !== null) asyncOptions.value = results;
  isSearching.value = false;
};

const debouncedAsyncSearch = debounce(performAsyncSearch, 300);

const onAsyncSearch = query => {
  const hasQuery = !!query.trim();
  lastSearchQuery.value = query;
  if (!hasQuery) asyncOptions.value = [];
  isSearching.value = hasQuery;
  debouncedAsyncSearch(query);
};

const resetModelOnAttributeKeyChange = newAttributeKey => {
  /**
   * Resets the filter values and operator when the attribute key changes. This ensures that
   * the values and operator remain compatible with the new attribute type. For example,
   * switching from a text field to a multi-select should reset the value from '' (empty string)
   * to an empty array.
   */
  const filter = getFilterFromFilterTypes(newAttributeKey);
  const newOperator = getOperator(filter, filterOperator.value);
  const newInputType = getInputType(newOperator, filter);
  if (['multiSelect', 'multiText'].includes(newInputType)) {
    values.value = [];
  } else if (
    ['searchSelect', 'asyncSearchSelect', 'booleanSelect'].includes(
      newInputType
    )
  ) {
    values.value = {};
  } else {
    values.value = '';
  }
  asyncOptions.value = [];
  filterOperator.value = newOperator.value;
};

watch([attributeKey, values, filterOperator], () => {
  showErrors.value = false;
});

const validate = () => {
  showErrors.value = true;
  return !validationError.value;
};

const resetValidation = () => {
  showErrors.value = false;
};

defineExpose({ validate, resetValidation });
</script>

<template>
  <li class="list-none">
    <div
      v-if="showQueryOperator"
      class="flex items-center gap-2 pb-2 ltr:pl-3 rtl:pr-3"
    >
      <span class="h-px w-3 bg-n-weak" aria-hidden="true" />
      <FilterSelect
        v-model="queryOperator"
        variant="faded"
        hide-icon
        class="text-xs"
        :options="queryOperatorOptions"
      />
      <span class="h-px flex-1 bg-n-weak" aria-hidden="true" />
    </div>
    <div
      class="flex flex-wrap gap-2 rounded-xl border bg-n-solid-2 p-2"
      :class="[
        showErrors && validationError
          ? 'animate-wiggle border-n-ruby-8'
          : 'border-n-weak dark:border-n-strong',
        inputType === 'multiText' ? 'items-start' : 'items-center',
      ]"
    >
      <span
        v-if="position"
        class="grid size-6 shrink-0 place-items-center rounded-md bg-n-alpha-2 text-[11px] font-semibold tabular-nums text-n-slate-11"
        aria-hidden="true"
      >
        {{ position }}
      </span>
      <FilterSelect
        v-model="attributeKey"
        variant="faded"
        :options="filterTypes"
        @update:model-value="resetModelOnAttributeKeyChange"
      />
      <FilterSelect
        v-model="filterOperator"
        variant="ghost"
        :options="currentFilter?.filterOperators"
      />
      <template v-if="currentOperator?.hasInput">
        <MultiSelect
          v-if="inputType === 'multiSelect'"
          v-model="values"
          :options="currentFilter.options"
          dropdown-max-height="max-h-72"
        />
        <SingleSelect
          v-else-if="inputType === 'searchSelect'"
          v-model="values"
          :options="currentFilter.options"
          dropdown-max-height="max-h-64"
        />
        <SingleSelect
          v-else-if="inputType === 'asyncSearchSelect'"
          v-model="values"
          async-search
          :options="asyncOptions"
          :is-searching="isSearching"
          :search-placeholder="currentFilter.searchPlaceholder"
          dropdown-max-height="max-h-64"
          @search="onAsyncSearch"
        />
        <SingleSelect
          v-else-if="inputType === 'booleanSelect'"
          v-model="values"
          disable-search
          :options="booleanOptions"
        />
        <DateTimePicker
          v-else-if="inputType === 'date'"
          :value="selectedDate"
          picker-type="date"
          format="DD/MM/YYYY"
          clearable
          compact
          :append-to-body="false"
          :disable-past="false"
          :confirm-text="t('FILTER.SUBMIT_BUTTON_LABEL')"
          :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
          class="min-w-44 flex-1 sm:max-w-64"
          @change="updateDate"
        />
        <Input
          v-else
          v-model="values"
          :type="inputFieldType"
          size="sm"
          class="min-w-44 flex-1 sm:max-w-64"
          custom-input-class="outline-offset-0"
          :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
        />
      </template>
      <Button
        v-tooltip.top="t('FILTER.REMOVE_CONDITION')"
        sm
        ghost
        ruby
        icon="i-hugeicons-delete-02"
        class="flex-shrink-0 ltr:ml-auto rtl:mr-auto"
        :aria-label="t('FILTER.REMOVE_CONDITION')"
        @click.stop="emit('remove')"
      />
    </div>
    <span
      v-if="showErrors && validationError"
      class="mt-1 block text-xs text-n-ruby-11"
    >
      {{ t(`FILTER.ERRORS.${validationError}`) }}
    </span>
  </li>
</template>
